import { Alert, Box, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePayerAssignments } from '../hooks/usePayerAssignments';
import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import {
  CHECKOUT_SYNC_EVENT,
  dispatchCheckoutSynced,
  resolvePostCheckoutNavigation,
  type CheckoutSyncedDetail,
} from '../subscriptions/checkoutSyncEvents';
import { getProduct } from '../subscriptions/catalog';
import { normalizeEntitlementsMap, ownsProduct } from '../subscriptions/entitlements';
import {
  clearPendingCheckout,
  isIosNativeApp,
  readPendingCheckout,
  savePendingCheckout,
  subscribeNativeAppResumed,
  subscribeNativeIapPurchaseUpdates,
} from '../utils/nativeCheckoutBridge';

const BANNER_DELAY_MS = 60_000;

const NativeCheckoutReturnHandler: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { refetch } = usePayerAssignments();
  const { confirmCheckout, confirmIapCheckout } = useSubscriptionCheckout();
  const [showRefreshBanner, setShowRefreshBanner] = useState(false);
  const [hasPendingCheckout, setHasPendingCheckout] = useState(
    () => !!readPendingCheckout(),
  );
  const syncInFlight = useRef(false);

  const navigateAfterCheckout = useCallback(
    (detail: Parameters<typeof resolvePostCheckoutNavigation>[0]) => {
      const path = resolvePostCheckoutNavigation(detail);
      if (!path) {
        return;
      }
      navigate(path, {
        replace: true,
        ...(detail.productName
          ? {
              state: {
                checkoutSuccess: detail.productName,
                alreadyFulfilled: detail.alreadyFulfilled,
              },
            }
          : {}),
      });
    },
    [navigate],
  );

  const trySyncPendingCheckout = useCallback(async () => {
    if (!isIosNativeApp()) {
      return;
    }
    const pending = readPendingCheckout();
    if (!pending || syncInFlight.current) {
      return;
    }

    syncInFlight.current = true;
    try {
      if (pending.checkoutMethod === 'iap' && pending.signedTransaction) {
        const confirmResult = await confirmIapCheckout({
          signedTransaction: pending.signedTransaction,
          ...(pending.appAccountToken ? { appAccountToken: pending.appAccountToken } : {}),
          ...(pending.iapPurchaseId ? { purchaseId: pending.iapPurchaseId } : {}),
        });
        if (confirmResult) {
          clearPendingCheckout();
          setHasPendingCheckout(false);
          setShowRefreshBanner(false);
          await refetch();
          enqueueSnackbar(
            confirmResult.alreadyFulfilled
              ? `${confirmResult.productName} is already active.`
              : `Purchase complete — ${confirmResult.productName} is now unlocked.`,
            { variant: 'success' },
          );
          dispatchCheckoutSynced(confirmResult);
          return;
        }
      } else if (pending.sessionId) {
        const confirmResult = await confirmCheckout(pending.sessionId);
        if (confirmResult) {
          clearPendingCheckout();
          setHasPendingCheckout(false);
          setShowRefreshBanner(false);
          await refetch();
          enqueueSnackbar(
            confirmResult.alreadyFulfilled
              ? `${confirmResult.productName} is already active.`
              : `Purchase complete — ${confirmResult.productName} is now unlocked.`,
            { variant: 'success' },
          );
          dispatchCheckoutSynced(confirmResult);
          return;
        }
      }

      const refreshedAssignments = await refetch();
      const assignment = refreshedAssignments.find((item) =>
        pending.assignmentId !== undefined ? item.id === pending.assignmentId : true,
      );
      const entitlements = normalizeEntitlementsMap(assignment?.entitlements);
      if (assignment && ownsProduct(entitlements, pending.productId)) {
        clearPendingCheckout();
        setHasPendingCheckout(false);
        setShowRefreshBanner(false);
        const productName = getProduct(pending.productId)?.name ?? pending.productId;
        enqueueSnackbar(`Purchase complete — ${productName} is now unlocked.`, {
          variant: 'success',
        });
        dispatchCheckoutSynced({
          productId: pending.productId,
          assignmentId: assignment.id,
        });
      }
    } finally {
      syncInFlight.current = false;
    }
  }, [confirmCheckout, confirmIapCheckout, enqueueSnackbar, refetch]);

  useEffect(() => {
    if (!isIosNativeApp()) {
      return undefined;
    }
    const onSynced = (event: Event) => {
      const detail = (event as CustomEvent<CheckoutSyncedDetail>).detail;
      if (!detail?.assignmentId) {
        return;
      }
      navigateAfterCheckout(detail);
    };
    window.addEventListener(CHECKOUT_SYNC_EVENT, onSynced);
    return () => {
      window.removeEventListener(CHECKOUT_SYNC_EVENT, onSynced);
    };
  }, [navigateAfterCheckout]);

  useEffect(() => {
    if (!isIosNativeApp()) {
      return undefined;
    }

    const pending = readPendingCheckout();
    if (!pending) {
      setHasPendingCheckout(false);
      return undefined;
    }
    setHasPendingCheckout(true);

    void trySyncPendingCheckout();

    const bannerTimer = window.setTimeout(() => {
      if (readPendingCheckout()) {
        setShowRefreshBanner(true);
      }
    }, BANNER_DELAY_MS);

    const unsubscribeResume = subscribeNativeAppResumed(() => {
      void trySyncPendingCheckout();
    });

    const unsubscribeIap = subscribeNativeIapPurchaseUpdates((update) => {
      if (update.signedTransaction) {
        const current = readPendingCheckout();
        if (current) {
          savePendingFromUpdate(current, update);
          void trySyncPendingCheckout();
        }
      }
    });

    return () => {
      window.clearTimeout(bannerTimer);
      unsubscribeResume();
      unsubscribeIap();
    };
  }, [trySyncPendingCheckout]);

  if (!isIosNativeApp() || !showRefreshBanner || !hasPendingCheckout) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 88,
        zIndex: (theme) => theme.zIndex.snackbar,
      }}
    >
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={() => void trySyncPendingCheckout()}>
            Refresh purchases
          </Button>
        }
      >
        Finished your purchase? Tap refresh to unlock your content.
      </Alert>
    </Box>
  );
};

function savePendingFromUpdate(
  current: NonNullable<ReturnType<typeof readPendingCheckout>>,
  update: { signedTransaction?: string; purchaseId?: string },
) {
  if (!update.signedTransaction) {
    return;
  }
  savePendingCheckout({
    productId: current.productId,
    checkoutMethod: 'iap',
    signedTransaction: update.signedTransaction,
    ...(current.appAccountToken ? { appAccountToken: current.appAccountToken } : {}),
    ...(update.purchaseId ? { iapPurchaseId: update.purchaseId } : {}),
    ...(current.assignmentId !== undefined ? { assignmentId: current.assignmentId } : {}),
  });
}

export default NativeCheckoutReturnHandler;
